import { Pressable, Text, View } from 'react-native'
import { Icon, symbolName } from '@/src/components/ui/Icon'
import { colors } from '@/src/theme'
import { body3, headline2, label1 } from '@/src/theme/typography'
import { useTranslation } from 'react-i18next'

import { EXERCISE_GOAL_CARD_STYLE } from '../../constants/statistics.styles'

/** Figma: voice assessment empty CTA — 274×42, 8px radius, 8 / 32 padding. */
const EMPTY_STATE_CTA_STYLE = {
  alignSelf: 'center' as const,
  width: '100%' as const,
  maxWidth: 274,
  height: 42,
  borderRadius: 8,
  paddingTop: 8,
  paddingBottom: 8,
  paddingLeft: 32,
  paddingRight: 32,
  gap: 10,
}

interface VoiceAssessmentEmptyStateProps {
  onStartAssessment: () => void
  /** `embedded`: inner panel only (parent supplies card chrome). */
  variant?: 'standalone' | 'embedded'
}

export function VoiceAssessmentEmptyState({
  onStartAssessment,
  variant = 'standalone',
}: VoiceAssessmentEmptyStateProps) {
  const { t } = useTranslation(['statistics'])

  const isEmbedded = variant === 'embedded'

  return (
    <View
      className={`items-center px-4 ${isEmbedded ? 'py-6' : 'py-8'}`}
      style={
        isEmbedded
          ? undefined
          : { ...EXERCISE_GOAL_CARD_STYLE, backgroundColor: colors.background }
      }
    >
      <View
        accessibilityRole="image"
        accessibilityLabel={t(
          'statistics:voice_assessment_microphone_accessibility',
        )}
        className="rounded-full items-center justify-center mb-4"
        style={{
          width: 72,
          height: 72,
          backgroundColor: colors.avatarBg,
        }}
      >
        <Icon
          name={symbolName('mic', 'mic')}
          size={32}
          color={colors.textMuted}
        />
      </View>
      <Text
        style={[
          headline2,
          {
            color: colors.textHeader,
            textAlign: 'center',
            marginBottom: 8,
          },
        ]}
      >
        {t('statistics:voice_assessment_empty_title')}
      </Text>
      <Text
        style={[
          body3,
          {
            color: colors.textMuted,
            textAlign: 'center',
            marginBottom: 24,
            paddingHorizontal: 8,
          },
        ]}
      >
        {t('statistics:voice_assessment_empty_subtitle')}
      </Text>
      <Pressable
        onPress={onStartAssessment}
        className="items-center justify-center active:opacity-90"
        style={[EMPTY_STATE_CTA_STYLE, { backgroundColor: colors.primary }]}
      >
        <Text style={[label1, { color: colors.primaryForeground }]}>
          {t('statistics:voice_assessment_start_button')}
        </Text>
      </Pressable>
    </View>
  )
}
